import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import SprayStatusBadge from './SprayStatusBadge';
import { Droplets, Calendar, User, MapPin, Eye, Play, CheckCircle, RotateCcw } from 'lucide-react';
import type { SprayApplication } from '../../types/spray';
import { toNum, toNumOr } from '@/utils/numeric';

interface SprayCardProps {
  spray: SprayApplication;
  currentUserId?: number;
  onStart?: (id: number) => void;
  onResume?: (id: number) => void;
  onComplete?: (id: number) => void;
}

function SprayCard({ spray, currentUserId, onStart, onResume, onComplete }: SprayCardProps) {
  const navigate = useNavigate();
  const isAssignedOperator = currentUserId && spray.assigned_operator_id === currentUserId;

  const effectiveAreaHa = toNum(spray.effective_area_ha);
  const coveragePct = toNum(spray.spray_coverage_pct);
  const concentrationFactor = toNum(spray.concentration_factor);

  const mix = [...(spray.chemicals ?? [])]
    .sort((a, b) => Number(!!b.is_primary) - Number(!!a.is_primary));

  const targetSummary =spray.targets?.length
    ? spray.targets.map(t => t.target_name).join(', ')
    : '—';

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {spray.reference_number && (
                <span className="text-xs text-neutral-400 font-mono">{spray.reference_number}</span>
              )}
              <SprayStatusBadge status={spray.status} size="sm" />
              {/* Held over a withholding-period conflict — visible from the
                  list so a Spray Admin can see what is waiting on them. */}
              {spray.whp_override_status === 'pending' && (
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">
                  WHP — awaiting approval
                </span>
              )}
              {spray.whp_override_status === 'rejected' && (
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">
                  WHP — rejected
                </span>
              )}
            </div>
            <h3 className="text-base font-semibold text-neutral-900 truncate">{spray.title}</h3>
          </div>
          <div className="flex-shrink-0 ml-3">
            <div className="h-9 w-9 rounded-lg bg-green-100 flex items-center justify-center">
              <Droplets className="h-5 w-5 text-green-600" />
            </div>
          </div>
        </div>

        {/* Chemicals — every product in the mix on its own line, primary
            first, with its own rate and target. An older server sends no mix,
            so fall back to the primary's name alone. */}
        {mix.length > 0 ? (
          <ul className="space-y-1 mb-2">
            {mix.map(c => (
              <li key={c.id} className="flex items-start justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <span className="block text-neutral-700 font-medium">{c.product_name}</span>
                  {c.target_pest && (
                    <span className="block text-xs text-neutral-400">{c.target_pest}</span>
                  )}
                </div>
                {c.rate != null && (
                  <span className="text-neutral-600 shrink-0">
                    {toNumOr(c.rate) * toNumOr(c.concentration_factor, 1)} {c.rate_unit}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-neutral-700 font-medium mb-1">
            {spray.chemical_name}
            {spray.chemical_trade_name && (
              <span className="text-neutral-400 font-normal"> ({spray.chemical_trade_name})</span>
            )}
          </p>
        )}

        {/* Targets */}
        {targetSummary !== '—' && (
          <p className="text-xs text-neutral-500 flex items-center gap-1 mb-2 truncate">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
            {targetSummary}
          </p>
        )}

        {/* Coverage info */}
        <div className="flex flex-wrap gap-3 text-xs text-neutral-500 mb-3">
          {effectiveAreaHa != null && (
            <span>{effectiveAreaHa.toFixed(1)} ha effective</span>
          )}
          {coveragePct != null && coveragePct < 100 && (
            <span className="text-amber-600">{coveragePct.toFixed(0)}% coverage</span>
          )}
          {concentrationFactor != null && concentrationFactor !== 1 && (
            <span className="text-purple-600">{concentrationFactor}× rate</span>
          )}
        </div>

        {/* Schedule & operator */}
        <div className="grid grid-cols-2 gap-2 text-xs text-neutral-500 mb-3">
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {spray.scheduled_date}
            {spray.scheduled_start_time && ` ${spray.scheduled_start_time}`}
          </div>
          {spray.operator_name && (
            <div className="flex items-center gap-1 truncate">
              <User className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{spray.operator_name}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-3 border-t border-neutral-100">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/spray/${spray.id}`)}
            className="flex-1"
          >
            <Eye className="h-4 w-4" />
            View
          </Button>

          {isAssignedOperator && spray.status === 'assigned' && onStart && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onStart(spray.id)}
              className="flex-1"
            >
              <Play className="h-4 w-4" />
              Start
            </Button>
          )}

          {isAssignedOperator && spray.status === 'paused' && onResume && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onResume(spray.id)}
              className="flex-1"
            >
              <RotateCcw className="h-4 w-4" />
              Resume
            </Button>
          )}

          {isAssignedOperator && spray.status === 'in_progress' && onComplete && (
            <Button
              size="sm"
              onClick={() => onComplete(spray.id)}
              className="flex-1 bg-success-600 hover:bg-success-700 text-white"
            >
              <CheckCircle className="h-4 w-4" />
              Complete
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default SprayCard;
